import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Mail, ListChecks, FileText, Rocket, Loader2, SlidersHorizontal, BarChart3, Ban, Plus, Trash2, RefreshCw, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { groupTemplatesByFunnel } from '../utils/marketingFunnels';
import { buildSampleMergeContext, renderMergeTemplate, MERGE_TAG_HINT, hasMergeTags } from '../utils/marketingMergeTags';
import {
  PROTECTED_EMAIL_LIST_NAMES,
  SOURCE_MANUAL,
  sourceLabel,
  SUBSCRIBER_SOURCE_OPTIONS,
  normalizeContactSource,
} from '../utils/marketingContactSources';

const EmailMarketingDashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const API = (process.env.REACT_APP_BACKEND_URL && process.env.REACT_APP_BACKEND_URL !== '')
    ? process.env.REACT_APP_BACKEND_URL
    : '';

  /** Same-origin /api in dev (setupProxy); absolute URL when REACT_APP_BACKEND_URL is set. */
  const apiPath = (path) => {
    const normalized = path.startsWith('/api') ? path : `/api/${path.replace(/^\//, '')}`;
    const base = API.replace(/\/$/, '');
    return base ? `${base}${normalized}` : normalized;
  };
  const [websites, setWebsites] = useState([]);
  const [selectedWebsiteId, setSelectedWebsiteId] = useState('');
  const [usesAccountScope, setUsesAccountScope] = useState(false);
  const [websitesLoading, setWebsitesLoading] = useState(false);

  const [activeTab, setActiveTab] = useState('settings');
  const [loading, setLoading] = useState(false);

  // Settings
  const [provider, setProvider] = useState('smtp');
  const [smtpHost, setSmtpHost] = useState('');
  const [smtpPort, setSmtpPort] = useState('587');
  const [smtpUser, setSmtpUser] = useState('');
  const [smtpPass, setSmtpPass] = useState('');
  const [smtpUseTls, setSmtpUseTls] = useState(true);
  const [fromNameDefault, setFromNameDefault] = useState('');
  const [fromEmailDefault, setFromEmailDefault] = useState('');
  const [sendWelcome, setSendWelcome] = useState(false);
  // Provider-specific API settings
  const [sendgridApiKey, setSendgridApiKey] = useState('');
  const [mailgunDomain, setMailgunDomain] = useState('');
  const [mailgunApiKey, setMailgunApiKey] = useState('');
  const [sesAccessKeyId, setSesAccessKeyId] = useState('');
  const [sesSecretAccessKey, setSesSecretAccessKey] = useState('');
  const [sesRegion, setSesRegion] = useState('');


  // Lists & subscribers
  const [lists, setLists] = useState([]);
  const [allSubscribers, setAllSubscribers] = useState([]);
  const [subscribersLoading, setSubscribersLoading] = useState(false);
  const [listName, setListName] = useState('');
  const [listDescription, setListDescription] = useState('');
  const [subEmail, setSubEmail] = useState('');
  const [subFirst, setSubFirst] = useState('');
  const [subLast, setSubLast] = useState('');
  const [subSourceFilter, setSubSourceFilter] = useState('');
  const [subListId, setSubListId] = useState('');

  // Templates
  const [templates, setTemplates] = useState([]);
  const [tplName, setTplName] = useState('');
  const [tplSubject, setTplSubject] = useState('');
  const [tplHtml, setTplHtml] = useState('');
  const [editingTemplateId, setEditingTemplateId] = useState('');
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiTone, setAiTone] = useState('friendly');
  const [aiGoal, setAiGoal] = useState('promotional');
  const [aiGenerating, setAiGenerating] = useState(false);
  const [audienceSyncInfo, setAudienceSyncInfo] = useState(null);
  const [audienceSyncing, setAudienceSyncing] = useState(false);
  const [includeAccountInSync, setIncludeAccountInSync] = useState(false);

  // Campaigns
  const [campaigns, setCampaigns] = useState([]);
  const [campName, setCampName] = useState('');
  const [campSubject, setCampSubject] = useState('');
  const [campFromName, setCampFromName] = useState('');
  const [campFromEmail, setCampFromEmail] = useState('');
  const [campHtml, setCampHtml] = useState('');
  const [campTemplateId, setCampTemplateId] = useState('');
  const [campListId, setCampListId] = useState('');
  const [campAbTest, setCampAbTest] = useState(false);
  const [campAbSubjectB, setCampAbSubjectB] = useState('');
  const [campScheduleMode, setCampScheduleMode] = useState('now');
  const [campScheduledAt, setCampScheduledAt] = useState('');
  const [sendingId, setSendingId] = useState(null);
  const [funnelRuns, setFunnelRuns] = useState([]);
  const [startingFunnelSlug, setStartingFunnelSlug] = useState('');

  // Analytics
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  // Blacklist
  const [blacklist, setBlacklist] = useState([]);
  const [blacklistEmail, setBlacklistEmail] = useState('');
  const [blacklistReason, setBlacklistReason] = useState('manual');
  const [blacklistSaving, setBlacklistSaving] = useState(false);

  const token = localStorage.getItem('token');

  const isMaskedSecret = (value) => {
    const v = String(value || '').trim();
    return !v || v === '••••••••' || /^•+$/.test(v);
  };

  const apiFetch = async (url, options = {}) => {
    const res = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(options.headers || {}),
        Authorization: `Bearer ${token}`,
      },
    });
    const data = await res.json().catch(() => null);
    if (!res.ok) {
      const detail = data?.detail;
      let message = 'Request failed';
      if (typeof detail === 'string') message = detail;
      else if (Array.isArray(detail)) message = detail.map((d) => d.msg || String(d)).join(', ');
      else if (res.status === 404 && (!detail || detail === 'Not Found')) {
        message = 'API not found — restart the backend server, then try again.';
      } else if (res.status === 405) {
        message = 'Method not allowed — restart the backend server, then try Generate draft again.';
      } else if (res.status) message = `Request failed (${res.status})`;
      throw new Error(message);
    }
    return data;
  };

  const buildSettingsPayload = () => {
    const body = {
      provider,
      smtp_host: smtpHost,
      smtp_port: Number(smtpPort) || 587,
      smtp_username: smtpUser,
      smtp_use_tls: smtpUseTls,
      from_name: fromNameDefault,
      from_email: fromEmailDefault,
      send_welcome_email: sendWelcome,
      sendgrid_api_key: sendgridApiKey,
      mailgun_domain: mailgunDomain,
      mailgun_api_key: mailgunApiKey,
      ses_access_key_id: sesAccessKeyId,
      ses_region: sesRegion,
    };
    if (!isMaskedSecret(smtpPass)) body.smtp_password = smtpPass;
    if (!isMaskedSecret(sesSecretAccessKey)) body.ses_secret_access_key = sesSecretAccessKey;
    return body;
  };

  const normalizeWebsites = (items = []) => (
    items
      .filter(Boolean)
      .map((website) => ({
        ...website,
        name: website.name || website.website_name || website.slug || website.custom_domain || 'Untitled website',
      }))
  );

  const buildEmailListsUrl = (websiteId) => (
    websiteId
      ? apiPath(`/websites/${websiteId}/subscriber-lists`)
      : apiPath('/marketing/email/subscriber-lists')
  );

  const buildEmailListDeleteUrl = (websiteId, listId) => (
    `${buildEmailListsUrl(websiteId)}/${listId}`
  );

  const buildEmailSubscriberUpdateUrl = (websiteId, subscriberId) => (
    websiteId
      ? apiPath(`/websites/${websiteId}/subscribers/${subscriberId}`)
      : apiPath(`/marketing/email/subscribers/${subscriberId}`)
  );

  const buildAudienceSyncUrl = (websiteId) => apiPath(`/websites/${websiteId}/marketing/audience-sync`);

  const buildEmailSubscribersUrl = (websiteId, listId = '') => {
    const base = websiteId
      ? apiPath(`/websites/${websiteId}/subscribers`)
      : apiPath('/marketing/email/subscribers');
    return listId ? `${base}?list_id=${encodeURIComponent(listId)}` : base;
  };

  const buildEmailTemplatesUrl = (websiteId) => (
    websiteId
      ? apiPath(`/websites/${websiteId}/email-templates`)
      : apiPath('/marketing/email/templates')
  );

  const buildEmailTemplateAiUrl = (websiteId) => (
    websiteId
      ? apiPath(`/websites/${websiteId}/email/ai-generate-template`)
      : apiPath('/marketing/email/ai-generate-template')
  );

  const buildEmailCampaignsUrl = (websiteId) => (
    websiteId
      ? apiPath(`/websites/${websiteId}/campaigns`)
      : apiPath('/marketing/email/campaigns')
  );

  const buildEmailTemplateUpdateUrl = (websiteId, templateId) => (
    websiteId
      ? apiPath(`/websites/${websiteId}/email-templates/${templateId}`)
      : apiPath(`/marketing/email/templates/${templateId}`)
  );

  const buildEmailCampaignSendUrl = (websiteId, campaignId) => (
    websiteId
      ? apiPath(`/websites/${websiteId}/campaigns/${campaignId}/send`)
      : apiPath(`/marketing/email/campaigns/${campaignId}/send`)
  );

  const buildFunnelRunsUrl = (websiteId) => (
    websiteId
      ? apiPath(`/websites/${websiteId}/funnel-runs`)
      : apiPath('/marketing/email/funnel-runs')
  );

  const buildEmailAnalyticsUrl = (websiteId) => (
    websiteId
      ? apiPath(`/websites/${websiteId}/email-marketing/analytics?days=7`)
      : apiPath('/marketing/email/analytics?days=7')
  );

  const buildEmailBlacklistUrl = (websiteId, entryId = '') => {
    const base = websiteId
      ? apiPath(`/websites/${websiteId}/email-marketing/blacklist`)
      : apiPath('/marketing/email/blacklist');
    return entryId ? `${base}/${entryId}` : base;
  };

  const loadWebsites = async () => {
    if (!token) return;
    setWebsitesLoading(true);
    try {
      const data = normalizeWebsites(await apiFetch(`${API}/api/websites`));
      setWebsites(data);
      if (selectedWebsiteId && !data.some((website) => website.id === selectedWebsiteId)) {
        setSelectedWebsiteId('');
      }
    } catch (e) {
      console.error(e);
      toast.error(e.message || 'Failed to load websites');
    } finally {
      setWebsitesLoading(false);
    }
  };

  const loadEmailData = async (websiteId) => {
    if (!token) return;
    setLoading(true);
    try {
      const settingsUrl = websiteId
        ? apiPath(`/websites/${websiteId}/marketing-settings`)
        : apiPath('/marketing/email/settings');
      const [settingsData, listsData, templatesData, campaignsData, funnelRunsData] = await Promise.all([
        apiFetch(settingsUrl),
        apiFetch(buildEmailListsUrl(websiteId)),
        apiFetch(buildEmailTemplatesUrl(websiteId)),
        apiFetch(buildEmailCampaignsUrl(websiteId)),
        apiFetch(buildFunnelRunsUrl(websiteId)).catch(() => ({ enrollments: [] })),
      ]);

      if (websiteId) {
        try {
          const sync = await apiFetch(buildAudienceSyncUrl(websiteId));
          setAudienceSyncInfo(sync);
        } catch {
          setAudienceSyncInfo(null);
        }
      } else {
        setAudienceSyncInfo(null);
      }

      // Settings
      const s = settingsData || {};
      setProvider(s.provider || 'smtp');
      // Provider-specific settings
      setSendgridApiKey(s.sendgrid_api_key || '');
      setMailgunDomain(s.mailgun_domain || '');
      setMailgunApiKey(s.mailgun_api_key || '');
      setSesAccessKeyId(s.ses_access_key_id || '');
      setSesSecretAccessKey(s.ses_secret_access_key || '');
      setSesRegion(s.ses_region || '');

      setSmtpHost(s.smtp_host || '');
      setSmtpPort(String(s.smtp_port || '587'));
      setSmtpUser(s.smtp_username || '');
      setSmtpPass('');
      setSmtpUseTls(s.smtp_use_tls ?? true);
      setFromNameDefault(s.from_name || '');
      setFromEmailDefault(s.from_email || '');
      setSendWelcome(s.send_welcome_email ?? false);

      setLists(listsData || []);
      setTemplates(templatesData || []);
      setCampaigns(campaignsData || []);
      setFunnelRuns(funnelRunsData?.enrollments || []);

      const crmLeadsList = (listsData || []).find((list) => list.name === 'CRM & Leads');
      const defaultListId = crmLeadsList?.id || listsData?.[0]?.id || '';
      const nextCampaignListId = listsData?.some((list) => list.id === campListId)
        ? campListId
        : defaultListId;

      // Only clear the subscriber filter if the selected list was deleted — never force a default.
      if (subListId && !(listsData || []).some((list) => list.id === subListId)) {
        setSubListId('');
      }
      if (nextCampaignListId !== campListId) setCampListId(nextCampaignListId);

      await loadSubscribers(websiteId);
    } catch (e) {
      console.error(e);
      toast.error(e.message || 'Failed to load email marketing data');
    } finally {
      setLoading(false);
    }
  };

  const buildSubscriberListCounts = (rows = []) => {
    const counts = {};
    rows.forEach((subscriber) => {
      (subscriber.lists || []).forEach((listId) => {
        counts[listId] = (counts[listId] || 0) + 1;
      });
    });
    return counts;
  };

  const subscriberListCounts = useMemo(
    () => buildSubscriberListCounts(allSubscribers),
    [allSubscribers],
  );

  const displayedSubscribers = useMemo(() => {
    let rows = allSubscribers;
    if (subListId) {
      rows = rows.filter((subscriber) => (subscriber.lists || []).includes(subListId));
    }
    if (subSourceFilter) {
      rows = rows.filter(
        (subscriber) => normalizeContactSource(subscriber.source) === subSourceFilter,
      );
    }
    return rows;
  }, [allSubscribers, subListId, subSourceFilter]);

  const loadSubscribers = async (websiteId) => {
    if (!token) return;
    setSubscribersLoading(true);
    try {
      const data = await apiFetch(buildEmailSubscribersUrl(websiteId, ''));
      setAllSubscribers(data || []);
    } catch (e) {
      console.error(e);
      toast.error(e.message || 'Failed to load subscribers');
    } finally {
      setSubscribersLoading(false);
    }
  };

  const handleSelectSubscriberList = (listId) => {
    setSubListId(listId);
  };

  const handleForceAudienceSync = async () => {
    if (!selectedWebsiteId) return;
    setAudienceSyncing(true);
    try {
      const sync = await apiFetch(buildAudienceSyncUrl(selectedWebsiteId), {
        method: 'POST',
        body: JSON.stringify({ include_account: includeAccountInSync }),
      });
      setAudienceSyncInfo(sync);
      await loadEmailData(selectedWebsiteId);
      await loadSubscribers(selectedWebsiteId);
      toast.success(sync.message || 'Synced CRM leads into this website');
    } catch (e) {
      toast.error(e.message || 'Failed to sync audience');
    } finally {
      setAudienceSyncing(false);
    }
  };

  useEffect(() => {
    const tab = searchParams.get('tab');
    const allowed = ['settings', 'lists', 'templates', 'campaigns', 'analytics', 'blacklist'];
    if (tab && allowed.includes(tab)) {
      setActiveTab(tab);
    }
    const websiteFromUrl = searchParams.get('website');
    if (websiteFromUrl) {
      setSelectedWebsiteId(websiteFromUrl);
    }
    const campaignFromUrl = searchParams.get('campaign');
    if (campaignFromUrl) {
      setActiveTab('campaigns');
      // Scroll/highlight after campaigns render
      window.setTimeout(() => {
        const el = document.querySelector(`[data-campaign-id="${CSS.escape(campaignFromUrl)}"]`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.classList.add('ring-2', 'ring-[#fbbf0f]');
        }
      }, 400);
    }
  }, [searchParams]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      loadWebsites();
    }, 0);
    return () => window.clearTimeout(timer);
  }, [token]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setUsesAccountScope(!selectedWebsiteId);
      loadEmailData(selectedWebsiteId);
    }, 0);
    return () => window.clearTimeout(timer);
  }, [selectedWebsiteId]);

  const loadAnalytics = async (websiteId) => {
    if (!token) return;
    setAnalyticsLoading(true);
    try {
      const data = await apiFetch(buildEmailAnalyticsUrl(websiteId));
      setAnalytics(data);
    } catch (e) {
      toast.error(e.message || 'Failed to load analytics');
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const loadBlacklist = async (websiteId) => {
    if (!token) return;
    try {
      const data = await apiFetch(buildEmailBlacklistUrl(websiteId));
      setBlacklist(data || []);
    } catch (e) {
      toast.error(e.message || 'Failed to load blacklist');
    }
  };

  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (activeTab === 'analytics') loadAnalytics(selectedWebsiteId);
      if (activeTab === 'blacklist') loadBlacklist(selectedWebsiteId);
    }, 0);
    return () => window.clearTimeout(timer);
  }, [activeTab, selectedWebsiteId]);

  const addBlacklist = async () => {
    if (!blacklistEmail.trim()) return;
    setBlacklistSaving(true);
    try {
      const data = await apiFetch(buildEmailBlacklistUrl(selectedWebsiteId), {
        method: 'POST',
        body: JSON.stringify({ email: blacklistEmail.trim(), reason: blacklistReason }),
      });
      setBlacklist((prev) => {
        const exists = prev.find((e) => e.email === data.email);
        return exists ? prev.map((p) => (p.email === data.email ? data : p)) : [data, ...prev];
      });
      setBlacklistEmail('');
      toast.success('Added to suppression list');
    } catch (e) {
      toast.error(e.message || 'Failed to add to blacklist');
    } finally {
      setBlacklistSaving(false);
    }
  };

  const removeBlacklist = async (entryId) => {
    try {
      await apiFetch(buildEmailBlacklistUrl(selectedWebsiteId, entryId), { method: 'DELETE' });
      setBlacklist((prev) => prev.filter((e) => e.id !== entryId));
      toast.success('Removed');
    } catch (e) {
      toast.error(e.message || 'Failed to remove');
    }
  };

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    try {
      const body = buildSettingsPayload();
      const targetUrl = selectedWebsiteId
        ? apiPath(`/websites/${selectedWebsiteId}/marketing-settings`)
        : apiPath('/marketing/email/settings');
      await apiFetch(targetUrl, { method: 'POST', body: JSON.stringify(body) });
      toast.success(selectedWebsiteId ? 'Website email settings saved' : 'Account email settings saved');
    } catch (e) {
      toast.error(e.message || 'Failed to save settings');
    }
  };

  const handleTestEmail = async () => {
    if (!fromEmailDefault.trim()) {
      toast.error('Set Default From Email before sending a test.');
      return;
    }
    if (!selectedWebsiteId) {
      toast.error('Select a website in the dropdown (top right) before sending a test email.');
      return;
    }
    const toEmail = window.prompt('Send test email to:', fromEmailDefault.trim());
    if (!toEmail?.trim()) return;

    const settingsPayload = buildSettingsPayload();
    const saveUrl = apiPath(`/websites/${selectedWebsiteId}/marketing-settings`);
    const testCandidates = [
      {
        url: apiPath(`/websites/${selectedWebsiteId}/marketing-settings/test-email`),
        body: { to_email: toEmail.trim(), settings: settingsPayload },
      },
      {
        url: apiPath(`/websites/${selectedWebsiteId}/operator-integrations/test-email`),
        body: { to_email: toEmail.trim(), settings: { email: settingsPayload } },
      },
    ];

    try {
      await apiFetch(saveUrl, { method: 'POST', body: JSON.stringify(settingsPayload) });

      let lastError = null;
      for (const candidate of testCandidates) {
        try {
          const res = await apiFetch(candidate.url, {
            method: 'POST',
            body: JSON.stringify(candidate.body),
          });
          toast.success(res.message || `Test email sent via ${res.provider || provider || 'smtp'}`);
          return;
        } catch (err) {
          lastError = err;
          const msg = err.message || '';
          const isMissingRoute = /not found|404|API not found/i.test(msg);
          if (!isMissingRoute) throw err;
        }
      }
      throw lastError || new Error('Test email API not found — restart the backend (python scripts/restart-backend.py) and refresh.');
    } catch (e) {
      const msg = e.message || 'Test email failed';
      if (/smtp|authentication|535|534|gmail/i.test(msg)) {
        toast.error(`${msg} — use a Gmail App Password (16 chars, no spaces), not your login password.`);
      } else {
        toast.error(msg);
      }
    }
  };

  const handleCreateList = async (e) => {
    e.preventDefault();
    if (!listName.trim()) return;
    try {
      const newList = await apiFetch(buildEmailListsUrl(selectedWebsiteId), {
        method: 'POST',
        body: JSON.stringify({ name: listName.trim(), description: listDescription.trim() }),
      });
      setLists((prev) => [newList, ...prev]);
      if (!campListId) setCampListId(newList.id);
      setListName('');
      setListDescription('');
      toast.success('List created');
    } catch (e) {
      toast.error(e.message || 'Failed to create list');
    }
  };

  const isProtectedEmailList = (list) => PROTECTED_EMAIL_LIST_NAMES.has(list?.name);

  const handleDeleteEmailList = async (list) => {
    if (!list?.id || isProtectedEmailList(list)) {
      toast.error(`${list?.name || 'This list'} is auto-managed and cannot be deleted`);
      return;
    }
    if (!window.confirm(`Delete list "${list.name}"? Subscribers remain but are removed from this list.`)) {
      return;
    }
    try {
      await apiFetch(buildEmailListDeleteUrl(selectedWebsiteId, list.id), { method: 'DELETE' });
      setLists((prev) => prev.filter((l) => l.id !== list.id));
      if (subListId === list.id) setSubListId('');
      if (campListId === list.id) setCampListId('');
      await loadEmailData(selectedWebsiteId);
      toast.success('List deleted');
    } catch (e) {
      toast.error(e.message || 'Failed to delete list');
    }
  };

  const handleAddSubscriber = async (e) => {
    e.preventDefault();
    if (!subEmail.trim()) return;
    const listIdForAdd = subListId || (lists.length === 1 ? lists[0].id : '');
    if (lists.length > 0 && !listIdForAdd) {
      toast.error('Select a list first (click TestList in the left panel), then add the subscriber.');
      return;
    }
    try {
      const newSub = await apiFetch(buildEmailSubscribersUrl(selectedWebsiteId), {
        method: 'POST',
        body: JSON.stringify({
          email: subEmail.trim(),
          first_name: subFirst.trim(),
          last_name: subLast.trim(),
          lists: listIdForAdd ? [listIdForAdd] : [],
          source: SOURCE_MANUAL,
        }),
      });
      setAllSubscribers((prev) => [newSub, ...prev]);
      setSubEmail('');
      setSubFirst('');
      setSubLast('');
      toast.success(listIdForAdd ? `Subscriber added to ${lists.find((l) => l.id === listIdForAdd)?.name || 'list'}` : 'Subscriber added');
    } catch (e) {
      toast.error(e.message || 'Failed to add subscriber');
    }
  };

  const assignSubscriberToList = async (subscriber, listId) => {
    if (!subscriber?.id || !listId) return;
    try {
      const listsForSub = Array.from(new Set([...(subscriber.lists || []), listId]));
      const updated = await apiFetch(
        buildEmailSubscriberUpdateUrl(selectedWebsiteId, subscriber.id),
        { method: 'PUT', body: JSON.stringify({ lists: listsForSub }) },
      );
      setAllSubscribers((prev) => prev.map((s) => (s.id === subscriber.id ? updated : s)));
      toast.success(`${subscriber.email} added to ${lists.find((l) => l.id === listId)?.name || 'list'}`);
    } catch (e) {
      toast.error(e.message || 'Failed to update list membership');
    }
  };

  const removeSubscriberFromList = async (subscriber, listId) => {
    if (!subscriber?.id || !listId) return;
    try {
      const listsForSub = (subscriber.lists || []).filter((id) => id !== listId);
      const updated = await apiFetch(
        buildEmailSubscriberUpdateUrl(selectedWebsiteId, subscriber.id),
        { method: 'PUT', body: JSON.stringify({ lists: listsForSub }) },
      );
      setAllSubscribers((prev) => prev.map((s) => (s.id === subscriber.id ? updated : s)));
      toast.success(`${subscriber.email} removed from ${lists.find((l) => l.id === listId)?.name || 'list'}`);
    } catch (e) {
      toast.error(e.message || 'Failed to update list membership');
    }
  };

  const handleDeleteSubscriber = async (subscriber) => {
    if (!subscriber?.id) return;
    if (!window.confirm(`Delete subscriber ${subscriber.email}?`)) return;
    try {
      await apiFetch(buildEmailSubscriberUpdateUrl(selectedWebsiteId, subscriber.id), { method: 'DELETE' });
      setAllSubscribers((prev) => prev.filter((s) => s.id !== subscriber.id));
      toast.success('Subscriber deleted');
    } catch (e) {
      toast.error(e.message || 'Failed to delete subscriber');
    }
  };

  const campaignRecipientPreview = () => {
    if (!campListId) return 'Select a list — campaign sends only to subscribers on that list.';
    const onList = allSubscribers.filter(
      (s) => s.status === 'subscribed' && (s.lists || []).includes(campListId),
    );
    if (!onList.length) {
      return 'No subscribed recipients on this list yet — add subscribers to the list first.';
    }
    return `Will send to: ${onList.map((s) => s.email).join(', ')}`;
  };

  const handleSaveTemplate = async (e) => {
    e.preventDefault();
    if (!tplName.trim() || !tplSubject.trim()) return;
    try {
      if (editingTemplateId) {
        const updated = await apiFetch(buildEmailTemplateUpdateUrl(selectedWebsiteId, editingTemplateId), {
          method: 'PUT',
          body: JSON.stringify({
            name: tplName.trim(),
            subject: tplSubject.trim(),
            html_content: tplHtml,
            text_content: tplHtml.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim(),
          }),
        });
        setTemplates((prev) => prev.map((t) => (t.id === editingTemplateId ? { ...t, ...updated } : t)));
        setEditingTemplateId('');
        toast.success('Template updated — merge tags personalize when you send');
      } else {
        const tpl = await apiFetch(buildEmailTemplatesUrl(selectedWebsiteId), {
          method: 'POST',
          body: JSON.stringify({
            name: tplName.trim(),
            subject: tplSubject.trim(),
            html_content: tplHtml,
            category: 'marketing',
          }),
        });
        setTemplates((prev) => [tpl, ...prev]);
        toast.success('Template created');
      }
      setTplName('');
      setTplSubject('');
      setTplHtml('');
    } catch (err) {
      toast.error(err.message || 'Failed to save template');
    }
  };

  const handleEditEmailTemplate = (template) => {
    setEditingTemplateId(template.id || '');
    setTplName(template.name || '');
    setTplSubject(template.subject || '');
    setTplHtml(template.html_content || '');
    setPreviewTemplate(null);
  };

  const handleCreateTemplate = handleSaveTemplate;

  const handleGenerateTemplateWithAi = async () => {
    const prompt = aiPrompt.trim();
    if (!prompt) {
      toast.error('Describe the email you want (e.g. 20% summer sale for subscribers)');
      return;
    }
    setAiGenerating(true);
    try {
      const brand = fromNameDefault || currentWebsite?.name || '';
      const generated = await apiFetch(buildEmailTemplateAiUrl(selectedWebsiteId), {
        method: 'POST',
        body: JSON.stringify({
          prompt,
          tone: aiTone,
          goal: aiGoal,
          brand_name: brand,
        }),
      });
      setTplName(generated.name || tplName);
      setTplSubject(generated.subject || '');
      setTplHtml(generated.html_content || '');
      setEditingTemplateId('');
      toast.success('AI draft ready — review, edit, then Save Template');
    } catch (e) {
      toast.error(e.message || 'AI template generation failed');
    } finally {
      setAiGenerating(false);
    }
  };

  const handleCreateCampaign = async (e) => {
    e.preventDefault();
    if (!campName.trim() || !campSubject.trim() || !campFromEmail.trim()) return;
    if (campScheduleMode === 'schedule' && !campScheduledAt) {
      toast.error('Choose a date and time under Schedule send');
      return;
    }
    try {
      const payload = {
        name: campName.trim(),
        subject: campSubject.trim(),
        from_name: campFromName.trim(),
        from_email: campFromEmail.trim(),
        html_content: campHtml,
        template_id: campTemplateId || null,
        status: 'draft',
        list_ids: campListId ? [campListId] : [],
      };
      if (campScheduleMode === 'schedule' && campScheduledAt) {
        payload.scheduled_at = new Date(campScheduledAt).toISOString();
      }
      const camp = await apiFetch(buildEmailCampaignsUrl(selectedWebsiteId), {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      setCampaigns((prev) => [camp, ...prev]);
      setCampName('');
      setCampSubject('');
      setCampFromName('');
      setCampFromEmail('');
      setCampHtml('');
      setCampTemplateId('');
      setCampScheduleMode('now');
      setCampScheduledAt('');
      if (camp.status === 'scheduled') {
        toast.success('Campaign scheduled — sends automatically at the chosen time');
      } else {
        toast.success('Campaign created — names like {{first_name}} personalize on send');
      }
    } catch (e) {
      toast.error(e.message || 'Failed to create campaign');
    }
  };

  const handleStartFunnelRun = async (funnel) => {
    const listId = campListId || subListId || lists[0]?.id;
    if (!listId) {
      toast.error('Create a list with subscribers first, then start automated follow-ups');
      return;
    }
    const fromEmail = (campFromEmail.trim() || fromEmailDefault || '').trim();
    if (!fromEmail) {
      toast.error('Set From Email in Settings before starting automated follow-ups');
      return;
    }
    setStartingFunnelSlug(funnel.slug);
    try {
      const res = await apiFetch(buildFunnelRunsUrl(selectedWebsiteId), {
        method: 'POST',
        body: JSON.stringify({
          funnel_slug: funnel.slug,
          list_ids: [listId],
          from_name: campFromName.trim() || fromNameDefault,
          from_email: fromEmail,
          max_steps: 3,
        }),
      });
      toast.success(
        res.message || `Started automated follow-ups for ${res.enrolled} subscriber(s). Email 1 sends shortly; stops if they reply.`,
        { duration: 8000 },
      );
      await loadEmailData(selectedWebsiteId);
      setActiveTab('campaigns');
    } catch (e) {
      toast.error(e.message || 'Failed to start funnel run');
    } finally {
      setStartingFunnelSlug('');
    }
  };

  const handleSendCampaign = async (campaignId) => {
    setSendingId(campaignId);
    try {
      const res = await apiFetch(buildEmailCampaignSendUrl(selectedWebsiteId, campaignId), { method: 'POST' });
      const recipients = (res.sent_to || []).join(', ') || `${res.sent_count} recipient(s)`;
      if (res.status === 'partial') {
        toast.warning(`Sent to ${recipients}. Some failed: ${(res.errors || []).join('; ')}`);
      } else {
        toast.success(`Campaign sent to ${recipients}. Check inbox and Spam.`, { duration: 10000 });
      }
      await loadEmailData(selectedWebsiteId);
    } catch (e) {
      toast.error(e.message || 'Failed to send campaign');
    } finally {
      setSendingId(null);
    }
  };

  const currentWebsite = websites.find((w) => w.id === selectedWebsiteId) || null;
  const { funnels: emailFunnels, standalone: emailStandaloneTemplates } = groupTemplatesByFunnel(templates);
  const sampleMergeContext = buildSampleMergeContext({
    website: currentWebsite,
    fromName: fromNameDefault,
  });

  const previewRenderedSubject = previewTemplate
    ? renderMergeTemplate(previewTemplate.subject || '', sampleMergeContext)
    : '';
  const previewRenderedHtml = previewTemplate
    ? renderMergeTemplate(previewTemplate.html_content || '', sampleMergeContext)
    : '';

  const applyEmailTemplateToForm = (template) => {
    handleEditEmailTemplate(template);
  };

  const loadEmailTemplateInCampaign = (template) => {
    setCampName(template.funnel_name ? `${template.funnel_name} — ${template.name}` : template.name);
    setCampSubject(template.subject || '');
    setCampHtml(template.html_content || '');
    setCampTemplateId(template.id || '');
    setActiveTab('campaigns');
    toast.success('Funnel loaded — create campaign and send (names auto-fill per subscriber)');
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <header className="border-b border-zinc-800 bg-zinc-900/50 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/marketing-suite')}
              className="text-zinc-400 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center">
                <Mail className="w-5 h-5" />
              </div>
              <div>
                <h1 className="text-xl font-bold">Email Marketing</h1>
                <p className="text-xs text-zinc-500">Compact lists, templates and campaigns with provider settings</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-zinc-500 mr-1">Website</span>
            <select
              value={selectedWebsiteId}
              onChange={(e) => setSelectedWebsiteId(e.target.value)}
              className="h-8 rounded-md bg-zinc-900 border border-zinc-700 px-2 text-xs focus:outline-none focus:border-violet-500"
              data-testid="email-website-select"
            >
              <option value="">Account-wide</option>
              {websites.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.name}
                </option>
              ))}
            </select>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={loadWebsites}
              disabled={websitesLoading}
              className="h-8 w-8 text-zinc-400 hover:text-white"
              data-testid="email-refresh-websites-button"
            >
              <RefreshCw className={`w-4 h-4 ${websitesLoading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-6 space-y-6">
        {websites.length === 0 && (
          <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-200">
            <p className="font-medium">No website yet</p>
            <p className="text-amber-200/80 mt-1">
              You can still save account-level provider settings now. Create a website later when you&apos;re ready to use lists, templates, campaigns and analytics.
            </p>
            <Button
              size="sm"
              className="mt-3 bg-amber-500 hover:bg-amber-600 text-black"
              onClick={() => navigate('/dashboard')}
            >
              Create a website
            </Button>
          </div>
        )}
        <div className="rounded-xl border border-zinc-800 bg-zinc-900/50 px-4 py-3 text-xs text-zinc-300" data-testid="email-scope-banner">
          {usesAccountScope ? (
            <>
              <span className="font-semibold text-white">Account-wide workspace.</span> Lists, templates, campaigns, analytics and blacklist entries created here are shared at the account level.
            </>
          ) : (
            <>
              <span className="font-semibold text-white">Website workspace:</span> {currentWebsite?.name || 'Selected website'}
            </>
          )}
        </div>
        <div className="flex items-center gap-2 border-b border-zinc-800 pb-2">
          <button
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs ${
              activeTab === 'settings'
                ? 'bg-violet-600 text-white'
                : 'bg-zinc-900 text-zinc-400 hover:text-white hover:bg-zinc-800'
            }`}
            onClick={() => setActiveTab('settings')}
          >
            <SlidersHorizontal className="w-3 h-3" /> Settings
          </button>
          <button
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs ${
              activeTab === 'lists'
                ? 'bg-violet-600 text-white'
                : 'bg-zinc-900 text-zinc-400 hover:text-white hover:bg-zinc-800'
            }`}
            onClick={() => setActiveTab('lists')}
          >
            <ListChecks className="w-3 h-3" /> Lists & Subscribers
          </button>
          <button
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs ${
              activeTab === 'templates'
                ? 'bg-violet-600 text-white'
                : 'bg-zinc-900 text-zinc-400 hover:text-white hover:bg-zinc-800'
            }`}
            onClick={() => setActiveTab('templates')}
          >
            <FileText className="w-3 h-3" /> Templates
          </button>
          <button
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs ${
              activeTab === 'campaigns'
                ? 'bg-violet-600 text-white'
                : 'bg-zinc-900 text-zinc-400 hover:text-white hover:bg-zinc-800'
            }`}
            onClick={() => setActiveTab('campaigns')}
          >
            <Rocket className="w-3 h-3" /> Campaigns
          </button>
          <button
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs ${
              activeTab === 'analytics'
                ? 'bg-violet-600 text-white'
                : 'bg-zinc-900 text-zinc-400 hover:text-white hover:bg-zinc-800'
            }`}
            onClick={() => setActiveTab('analytics')}
            data-testid="email-tab-analytics"
          >
            <BarChart3 className="w-3 h-3" /> Analytics
          </button>
          <button
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs ${
              activeTab === 'blacklist'
                ? 'bg-violet-600 text-white'
                : 'bg-zinc-900 text-zinc-400 hover:text-white hover:bg-zinc-800'
            }`}
            onClick={() => setActiveTab('blacklist')}
            data-testid="email-tab-blacklist"
          >
            <Ban className="w-3 h-3" /> Blacklist
          </button>
        </div>

        {loading && (
          <div className="flex items-center gap-2 text-xs text-zinc-500">
            <Loader2 className="w-3 h-3 animate-spin" /> Loading...
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <section className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
              <h2 className="text-sm font-semibold mb-2">Email Provider</h2>
              <form onSubmit={handleSaveSettings} className="space-y-3 text-xs">
                {usesAccountScope && (
                  <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-[11px] text-amber-200" data-testid="email-settings-no-website-warning">
                    You&apos;re editing account-level email provider settings. These will be used until a website has its own overrides.
                  </div>
                )}
                <div>
                  <label className="block text-[11px] text-zinc-400 mb-1">Provider</label>
                  <select
                    value={provider}
                    onChange={(e) => setProvider(e.target.value)}
                    className="w-full h-8 rounded-md bg-zinc-900 border border-zinc-700 px-2 text-xs focus:outline-none focus:border-violet-500"
                    data-testid="email-provider-select"
                  >
                    <option value="smtp">SMTP (Generic / Mailtrap / Any SMTP)</option>
                    <option value="sendgrid">SendGrid HTTP API</option>
                    <option value="mailgun">Mailgun HTTP API</option>
                    <option value="ses">Amazon SES API</option>
                  </select>
                </div>
                {provider === 'smtp' && (
                  <>
                    <div className="rounded-lg border border-sky-500/30 bg-sky-500/10 px-3 py-2 text-[11px] text-sky-100">
                      <strong>Gmail:</strong> use host <code className="text-sky-200">smtp.gmail.com</code>, port <code className="text-sky-200">587</code>, TLS on.
                      Username = your Gmail address. Password = a 16-character <strong>App Password</strong> from Google Account → Security → 2-Step Verification → App passwords (not your normal Gmail password).
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-[11px] text-zinc-400 mb-1">SMTP Host</label>
                        <Input
                          value={smtpHost}
                          onChange={(e) => setSmtpHost(e.target.value)}
                          className="h-8 text-xs bg-zinc-900 border-zinc-700 focus:border-violet-500"
                          placeholder="smtp.yourprovider.com"
                          data-testid="email-smtp-host-input"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] text-zinc-400 mb-1">Port</label>
                        <Input
                          value={smtpPort}
                          onChange={(e) => setSmtpPort(e.target.value)}
                          className="h-8 text-xs bg-zinc-900 border-zinc-700 focus:border-violet-500"
                          placeholder="587"
                          data-testid="email-smtp-port-input"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-[11px] text-zinc-400 mb-1">Username</label>
                        <Input
                          value={smtpUser}
                          onChange={(e) => setSmtpUser(e.target.value)}
                          className="h-8 text-xs bg-zinc-900 border-zinc-700 focus:border-violet-500"
                          data-testid="email-smtp-username-input"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] text-zinc-400 mb-1">Password</label>
                        <Input
                          type="password"
                          value={smtpPass}
                          onChange={(e) => setSmtpPass(e.target.value)}
                          className="h-8 text-xs bg-zinc-900 border-zinc-700 focus:border-violet-500"
                          placeholder="Leave blank to keep existing"
                          data-testid="email-smtp-password-input"
                        />
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <label className="flex items-center gap-2 text-[11px] text-zinc-400">
                        <input
                          type="checkbox"
                          checked={smtpUseTls}
                          onChange={(e) => setSmtpUseTls(e.target.checked)}
                          className="rounded border-zinc-600 bg-zinc-900"
                          data-testid="email-smtp-tls-checkbox"
                        />
                        Use TLS (STARTTLS)
                      </label>
                    </div>
                  </>
                )}
                {provider === 'sendgrid' && (
                  <>
                    <div>
                      <label className="block text-[11px] text-zinc-400 mb-1">SendGrid API Key</label>
                      <Input
                        type="password"
                        value={sendgridApiKey}
                        onChange={(e) => setSendgridApiKey(e.target.value)}
                        className="h-8 text-xs bg-zinc-900 border-zinc-700 focus:border-violet-500"
                        placeholder="SG.xxxxx"
                        data-testid="email-sendgrid-api-key-input"
                      />
                      <p className="mt-1 text-[10px] text-zinc-500">
                        This key is stored per-website. It will not be shared across projects.
                      </p>
                    </div>
                  </>
                )}
                {provider === 'mailgun' && (
                  <>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-[11px] text-zinc-400 mb-1">Mailgun Domain</label>
                        <Input
                          value={mailgunDomain}
                          onChange={(e) => setMailgunDomain(e.target.value)}
                          className="h-8 text-xs bg-zinc-900 border-zinc-700 focus:border-violet-500"
                          placeholder="mg.yourdomain.com"
                          data-testid="email-mailgun-domain-input"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] text-zinc-400 mb-1">Mailgun API Key</label>
                        <Input
                          type="password"
                          value={mailgunApiKey}
                          onChange={(e) => setMailgunApiKey(e.target.value)}
                          className="h-8 text-xs bg-zinc-900 border-zinc-700 focus:border-violet-500"
                          placeholder="key-xxxx"
                          data-testid="email-mailgun-api-key-input"
                        />
                      </div>
                    </div>
                    <p className="mt-1 text-[10px] text-zinc-500">
                      Use your Mailgun sending domain and private API key.
                    </p>
                  </>
                )}
                {provider === 'ses' && (
                  <>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-[11px] text-zinc-400 mb-1">Access Key ID</label>
                        <Input
                          value={sesAccessKeyId}
                          onChange={(e) => setSesAccessKeyId(e.target.value)}
                          className="h-8 text-xs bg-zinc-900 border-zinc-700 focus:border-violet-500"
                          data-testid="email-ses-access-key-input"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] text-zinc-400 mb-1">Secret Access Key</label>
                        <Input
                          type="password"
                          value={sesSecretAccessKey}
                          onChange={(e) => setSesSecretAccessKey(e.target.value)}
                          className="h-8 text-xs bg-zinc-900 border-zinc-700 focus:border-violet-500"
                          data-testid="email-ses-secret-key-input"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      <div>
                        <label className="block text-[11px] text-zinc-400 mb-1">Region</label>
                        <Input
                          value={sesRegion}
                          onChange={(e) => setSesRegion(e.target.value)}
                          className="h-8 text-xs bg-zinc-900 border-zinc-700 focus:border-violet-500"
                          placeholder="us-east-1"
                          data-testid="email-ses-region-input"
                        />
                      </div>
                    </div>
                    <p className="mt-1 text-[10px] text-zinc-500">
                      Make sure your SES sender email is verified in this region.
                    </p>
                  </>
                )}
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <div>
                    <label className="block text-[11px] text-zinc-400 mb-1">Default From Name</label>
                    <Input
                      value={fromNameDefault}
                      onChange={(e) => setFromNameDefault(e.target.value)}
                      className="h-8 text-xs bg-zinc-900 border-zinc-700 focus:border-violet-500"
                      placeholder={currentWebsite?.name || 'Your Brand'}
                      data-testid="email-from-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] text-zinc-400 mb-1">Default From Email</label>
                    <Input
                      type="email"
                      value={fromEmailDefault}
                      onChange={(e) => setFromEmailDefault(e.target.value)}
                      className="h-8 text-xs bg-zinc-900 border-zinc-700 focus:border-violet-500"
                      placeholder="no-reply@yourdomain.com"
                      data-testid="email-from-email-input"
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2 mt-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-8 px-3 text-xs border-zinc-600"
                    onClick={handleTestEmail}
                    data-testid="email-test-settings-button"
                  >
                    Send test email
                  </Button>
                  <Button type="submit" size="sm" className="h-8 px-3 text-xs bg-violet-600 hover:bg-violet-500" data-testid="email-save-settings-button">
                    {selectedWebsiteId ? 'Save Website Settings' : 'Save Account Settings'}
                  </Button>
                </div>
              </form>
            </section>

            <section className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
              <h2 className="text-sm font-semibold mb-2">Automations</h2>
              <div className="space-y-3 text-xs">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[11px] text-zinc-300">Welcome email on new subscriber</p>
                    <p className="text-[11px] text-zinc-500">
                      Automatically send a welcome email when someone subscribes to your list.
                    </p>
                  </div>
                  <label className="flex items-center gap-2 text-[11px] text-zinc-400">
                    <input
                      type="checkbox"
                      checked={sendWelcome}
                      onChange={(e) => setSendWelcome(e.target.checked)}
                      className="rounded border-zinc-600 bg-zinc-900"
                    />
                    Enable
                  </label>
                </div>
                <p className="text-[11px] text-zinc-500">
                  (Current version uses your default From settings and a simple welcome content or backend-selected
                  template. Advanced automation builder can be added later.)
                </p>
              </div>
            </section>
          </div>
        )}

        {activeTab === 'lists' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {selectedWebsiteId && (
              <div
                className="md:col-span-2 rounded-xl border border-emerald-500/25 bg-emerald-500/10 px-4 py-3 text-xs text-emerald-100"
                data-testid="email-audience-sync-banner"
              >
                <p className="font-medium text-emerald-50">Website CRM leads sync into this site&apos;s email lists</p>
                <p className="text-emerald-100/80 mt-1">
                  By default, only <strong>website-scoped</strong> CRM leads and this site&apos;s subscribers are
                  copied into the <strong>CRM &amp; Leads</strong> list when you open Email Marketing or load lists.
                  Enable the option below to also merge account-wide contacts.
                </p>
                {audienceSyncInfo?.stats?.synced || audienceSyncInfo?.last_sync_at ? (
                  <p className="text-[10px] text-emerald-200/70 mt-2">
                    Last sync:{' '}
                    {audienceSyncInfo.last_sync_at
                      ? new Date(audienceSyncInfo.last_sync_at).toLocaleString()
                      : '—'}
                    {audienceSyncInfo.stats?.synced
                      ? ` · ${audienceSyncInfo.stats.synced} email(s) (${audienceSyncInfo.stats.created || 0} new, ${audienceSyncInfo.stats.updated || 0} updated)`
                      : ''}
                  </p>
                ) : null}
                <label className="mt-2 flex items-center gap-2 text-[10px] text-emerald-100/90 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeAccountInSync}
                    onChange={(e) => setIncludeAccountInSync(e.target.checked)}
                    data-testid="email-audience-include-account"
                    className="rounded border-emerald-600"
                  />
                  Also include account-wide CRM contacts and leads
                </label>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="mt-2 h-7 text-[10px] border-emerald-600 text-emerald-200 hover:bg-emerald-600/10"
                  disabled={audienceSyncing}
                  onClick={handleForceAudienceSync}
                  data-testid="email-audience-sync-now"
                >
                  {audienceSyncing ? 'Syncing…' : 'Sync website CRM leads'}
                </Button>
              </div>
            )}
            <section className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
              <h2 className="text-sm font-semibold mb-2">Create List</h2>
              <form onSubmit={handleCreateList} className="space-y-2 text-xs">
                <div>
                  <label className="block text-[11px] text-zinc-400 mb-1">Name</label>
                  <Input
                    value={listName}
                    onChange={(e) => setListName(e.target.value)}
                    className="h-8 text-xs bg-zinc-900 border-zinc-700 focus:border-violet-500"
                    placeholder="Newsletter Subscribers"
                  />
                </div>
                <div>
                  <label className="block text-[11px] text-zinc-400 mb-1">Description</label>
                  <Input
                    value={listDescription}
                    onChange={(e) => setListDescription(e.target.value)}
                    className="h-8 text-xs bg-zinc-900 border-zinc-700 focus:border-violet-500"
                    placeholder="Optional description"
                  />
                </div>
                <div className="flex justify-end">
                  <Button
                    type="submit"
                    size="sm"
                    className="h-8 px-3 text-xs bg-violet-600 hover:bg-violet-500"
                  >
                    Create List
                  </Button>
                </div>
              </form>

              <div className="mt-4 border border-zinc-800 rounded-lg overflow-hidden">
                <div className="px-3 py-2 bg-zinc-950/70 border-b border-zinc-800 text-[11px] text-zinc-400">
                  Lists
                </div>
                <div className="px-3 py-2 border-b border-zinc-800 bg-zinc-950/40 flex items-center justify-between gap-2 text-[11px]">
                  <button
                    type="button"
                    onClick={() => handleSelectSubscriberList('')}
                    className={`px-2 py-1 rounded-md border ${subListId ? 'border-zinc-700 text-zinc-400 hover:text-white' : 'border-violet-500/30 bg-violet-500/10 text-violet-200'}`}
                    data-testid="email-clear-list-filter-button"
                  >
                    All subscribers
                  </button>
                  <span className="text-zinc-500">
                    <strong className="text-zinc-400">CRM &amp; Leads</strong> is auto-synced from scraper/CRM.
                    {' '}
                    <strong className="text-zinc-400">CRM leads (all)</strong> is created when you send a CRM campaign.
                  </span>
                </div>
                <div className="max-h-64 overflow-y-auto">
                  {lists.length === 0 ? (
                    <p className="text-[11px] text-zinc-500 p-3">No lists yet.</p>
                  ) : (
                    <Table className="w-full text-[11px]">
                      <TableHeader className="bg-zinc-900/70">
                        <TableRow>
                          <TableHead className="px-3 py-2 text-left">Name</TableHead>
                          <TableHead className="px-3 py-2 text-left">Members</TableHead>
                          <TableHead className="px-3 py-2 text-left">Description</TableHead>
                          <TableHead className="px-3 py-2 text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {lists.map((l) => (
                          <TableRow
                            key={l.id}
                            className={`border-t border-zinc-800 cursor-pointer ${
                              subListId === l.id ? 'bg-zinc-900/70' : 'hover:bg-zinc-900/40'
                            }`}
                            onClick={() => handleSelectSubscriberList(l.id)}
                            data-testid={`email-list-row-${l.id}`}
                          >
                            <TableCell className="px-3 py-2 whitespace-nowrap">{l.name}</TableCell>
                            <TableCell className="px-3 py-2 whitespace-nowrap text-zinc-400">
                              {subscriberListCounts[l.id] ?? '—'}
                            </TableCell>
                            <TableCell className="px-3 py-2 text-zinc-500 whitespace-nowrap">
                              {l.description || '-'}
                            </TableCell>
                            <TableCell className="px-3 py-2 text-right">
                              {!isProtectedEmailList(l) && (
                                <button
                                  type="button"
                                  title="Delete list"
                                  className="p-1 text-zinc-500 hover:text-red-400"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteEmailList(l);
                                  }}
                                  data-testid={`email-delete-list-${l.id.slice(0, 8)}`}
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </div>
              </div>
            </section>

            <section className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
              <div className="flex items-center justify-between gap-2 mb-2">
                <h2 className="text-sm font-semibold">Subscribers</h2>
                <div className="flex items-center gap-2">
                  <select
                    value={subSourceFilter}
                    onChange={(e) => setSubSourceFilter(e.target.value)}
                    className="h-7 rounded-md bg-zinc-950 border border-zinc-800 px-2 text-[10px] text-zinc-300"
                    data-testid="email-subscriber-source-filter"
                  >
                    {SUBSCRIBER_SOURCE_OPTIONS.map((opt) => (
                      <option key={opt.value || 'all'} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <span className="text-[10px] text-zinc-500" data-testid="email-subscriber-filter-label">
                  {subscribersLoading ? (
                    'Refreshing…'
                  ) : subListId ? (
                    `Showing list: ${(lists.find((l) => l.id === subListId) || {}).name || 'Unknown'} (${displayedSubscribers.length})`
                  ) : (
                    `All subscribers (${displayedSubscribers.length})`
                  )}
                  </span>
                </div>
              </div>
              <form onSubmit={handleAddSubscriber} className="space-y-2 text-xs mb-3">
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="block text-[11px] text-zinc-400 mb-1">Email</label>
                    <Input
                      type="email"
                      value={subEmail}
                      onChange={(e) => setSubEmail(e.target.value)}
                      className="h-8 text-xs bg-zinc-900 border-zinc-700 focus:border-violet-500"
                      placeholder="user@example.com"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] text-zinc-400 mb-1">First Name</label>
                    <Input
                      value={subFirst}
                      onChange={(e) => setSubFirst(e.target.value)}
                      className="h-8 text-xs bg-zinc-900 border-zinc-700 focus:border-violet-500"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] text-zinc-400 mb-1">Last Name</label>
                    <Input
                      value={subLast}
                      onChange={(e) => setSubLast(e.target.value)}
                      className="h-8 text-xs bg-zinc-900 border-zinc-700 focus:border-violet-500"
                    />
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <div className="text-[11px] text-zinc-500">
                    {subListId
                      ? `Adding to list: ${(lists.find((l) => l.id === subListId) || {}).name || 'Unknown'}`
                      : lists.length > 0
                        ? 'Select a list on the left before adding — otherwise the subscriber will not receive campaigns.'
                        : 'Create a list first, then add subscribers.'}
                  </div>
                  <Button
                    type="submit"
                    size="sm"
                    className="h-8 px-3 text-xs bg-violet-600 hover:bg-violet-500"
                  >
                    Add Subscriber
                  </Button>
                </div>
              </form>

              <div className="border border-zinc-800 rounded-lg overflow-hidden">
                <div className="px-3 py-2 bg-zinc-950/70 border-b border-zinc-800 text-[11px] text-zinc-400">
                  Subscribers
                </div>
                <div className="max-h-72 overflow-y-auto">
                  {subscribersLoading && displayedSubscribers.length === 0 ? (
                    <p className="text-[11px] text-zinc-500 p-3 flex items-center gap-2">
                      <Loader2 className="w-3 h-3 animate-spin" /> Loading subscribers…
                    </p>
                  ) : displayedSubscribers.length === 0 ? (
                    <p className="text-[11px] text-zinc-500 p-3">
                      {subListId
                        ? `No subscribers on ${(lists.find((l) => l.id === subListId) || {}).name || 'this list'} yet.`
                        : 'No subscribers yet.'}
                    </p>
                  ) : (
                    <Table className="w-full text-[11px]">
                      <TableHeader className="bg-zinc-900/70">
                        <TableRow>
                          <TableHead className="px-3 py-2 text-left">Email</TableHead>
                          <TableHead className="px-3 py-2 text-left">Name</TableHead>
                          <TableHead className="px-3 py-2 text-left">Source</TableHead>
                          <TableHead className="px-3 py-2 text-left">Lists</TableHead>
                          <TableHead className="px-3 py-2 text-left">Status</TableHead>
                          <TableHead className="px-3 py-2 text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {displayedSubscribers.map((s) => (
                          <TableRow key={s.id} className="border-t border-zinc-800">
                            <TableCell className="px-3 py-2 whitespace-nowrap">{s.email}</TableCell>
                            <TableCell className="px-3 py-2 whitespace-nowrap">
                              {(s.first_name || s.last_name)
                                ? `${s.first_name || ''} ${s.last_name || ''}`.trim()
                                : '-'}
                            </TableCell>
                            <TableCell className="px-3 py-2 whitespace-nowrap text-[10px] text-zinc-400">
                              {sourceLabel(s.source)}
                            </TableCell>
                            <TableCell className="px-3 py-2 text-[10px] text-zinc-400">
                              {(s.lists || []).length
                                ? (
                                  subListId
                                    ? ((lists.find((l) => l.id === subListId) || {}).name || 'List')
                                    : (s.lists || []).map((lid) => lists.find((l) => l.id === lid)?.name || 'List').join(', ')
                                )
                                : (
                                  <span className="text-amber-400">
                                    No list
                                    {lists[0] && (
                                      <button
                                        type="button"
                                        className="ml-2 underline text-violet-300"
                                        onClick={() => assignSubscriberToList(s, lists[0].id)}
                                      >
                                        Add to {lists[0].name}
                                      </button>
                                    )}
                                  </span>
                                )}
                            </TableCell>
                            <TableCell className="px-3 py-2 whitespace-nowrap text-[10px] uppercase tracking-wide text-zinc-400">
                              {s.status}
                            </TableCell>
                            <TableCell className="px-3 py-2 whitespace-nowrap text-right">
                              <div className="flex items-center justify-end gap-1">
                                {subListId && (s.lists || []).includes(subListId) && (
                                  <button
                                    type="button"
                                    className="text-[10px] text-zinc-400 hover:text-amber-300 underline"
                                    onClick={() => removeSubscriberFromList(s, subListId)}
                                  >
                                    Remove
                                  </button>
                                )}
                                {subListId && !(s.lists || []).includes(subListId) && (
                                  <button
                                    type="button"
                                    className="text-[10px] text-violet-400 hover:text-violet-300 underline"
                                    onClick={() => assignSubscriberToList(s, subListId)}
                                  >
                                    Add
                                  </button>
                                )}
                                <button
                                  type="button"
                                  title="Delete subscriber"
                                  className="p-1 text-zinc-500 hover:text-red-400"
                                  onClick={() => handleDeleteSubscriber(s)}
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </div>
              </div>
            </section>
          </div>
        )}

        {activeTab === 'templates' && (
          <div className="space-y-6">
            {emailFunnels.length > 0 && (
              <section className="bg-zinc-900/50 border border-violet-500/20 rounded-xl p-4" data-testid="email-funnel-library">
                <h2 className="text-sm font-semibold mb-1">Automated email follow-ups</h2>
                <p className="text-[11px] text-zinc-500 mb-3">
                  Pre-written email series you can turn on for a whole list — like a mini drip campaign. {MERGE_TAG_HINT}
                </p>
                <div
                  className="mb-4 rounded-lg border border-violet-500/25 bg-violet-500/10 px-3 py-2.5 text-[11px] text-violet-100/90 space-y-1.5"
                  data-testid="email-funnel-explainer"
                >
                  <p className="font-medium text-violet-100">How it works for your subscribers</p>
                  <ul className="list-disc pl-4 space-y-1 text-violet-100/85">
                    <li>
                      <strong>Start automated follow-ups</strong> — sends email 1 now, then emails 2 and 3 on a delay (about 2 days, then 1 week).
                    </li>
                    <li>
                      <strong>Use</strong> — loads only one email into Campaigns so you can send or schedule it yourself.
                    </li>
                    <li>
                      Stops automatically if someone <strong>replies</strong> to your inbox (needs Gmail/IMAP in Settings).
                    </li>
                  </ul>
                  <p className="text-[10px] text-violet-200/70 pt-0.5">
                    Uses the list selected on the Campaigns tab (or your first list). Track progress under Campaigns → Automated follow-ups.
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {emailFunnels.map((funnel) => (
                    <div key={funnel.slug} className="border border-zinc-800 rounded-lg p-3 bg-zinc-950/40">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div>
                          <p className="text-xs font-semibold text-violet-200">{funnel.name}</p>
                          <p className="text-[10px] text-zinc-500">{funnel.description}</p>
                        </div>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="h-auto min-h-7 px-2 py-1 text-[10px] shrink-0 border-violet-600 text-violet-300 hover:bg-violet-600/10 text-left leading-tight"
                          disabled={startingFunnelSlug === funnel.slug}
                          onClick={() => handleStartFunnelRun(funnel)}
                          data-testid={`funnel-run-${funnel.slug}`}
                          title="Send 3 follow-up emails to everyone on your list, spaced over several days. Stops if they reply."
                        >
                          {startingFunnelSlug === funnel.slug ? (
                            'Starting…'
                          ) : (
                            <>
                              Start automated
                              <span className="block text-[9px] text-violet-200/70 font-normal">3 emails · auto schedule</span>
                            </>
                          )}
                        </Button>
                      </div>
                      <p className="text-[10px] text-zinc-500 mb-2">
                        {funnel.steps.length} emails in this series · personalize names with merge tags
                      </p>
                      <ul className="space-y-1.5">
                        {funnel.steps.map((step) => (
                          <li key={step.id} className="flex items-center justify-between gap-2 text-[11px] border border-zinc-800/80 rounded px-2 py-1.5">
                            <span className="truncate">
                              <span className="text-zinc-500">Step {step.funnel_step}/{step.funnel_step_total}</span> — {step.name}
                            </span>
                            <span className="shrink-0 flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => handleEditEmailTemplate(step)}
                                className="text-zinc-400 hover:text-zinc-100"
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                onClick={() => setPreviewTemplate(step)}
                                className="text-zinc-400 hover:text-zinc-100"
                                data-testid={`funnel-preview-${step.id}`}
                              >
                                Preview
                              </button>
                              <button
                                type="button"
                                onClick={() => loadEmailTemplateInCampaign(step)}
                                className="text-violet-300 hover:text-violet-100"
                                title="Load this single email into Campaigns to send or schedule manually"
                              >
                                Use once
                              </button>
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
                {previewTemplate && (
                  <div className="mt-4 border border-zinc-700 rounded-lg overflow-hidden bg-white" data-testid="email-funnel-preview">
                    <div className="flex items-center justify-between px-3 py-2 bg-zinc-100 border-b border-zinc-200 text-xs text-zinc-700">
                      <span>
                        Preview: <strong>{previewTemplate.name}</strong>
                        <span className="text-zinc-500 ml-2">Subject: {previewRenderedSubject}</span>
                      </span>
                      <button type="button" onClick={() => setPreviewTemplate(null)} className="text-zinc-500 hover:text-zinc-800">
                        Close
                      </button>
                    </div>
                    <iframe
                      title="Email preview"
                      srcDoc={previewRenderedHtml}
                      className="w-full h-96 border-0 bg-white"
                      sandbox=""
                    />
                  </div>
                )}
              </section>
            )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <section className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
              <h2 className="text-sm font-semibold mb-1">{editingTemplateId ? 'Edit Template' : 'Create Template'}</h2>
              <p className="text-[10px] text-zinc-500 mb-2">{MERGE_TAG_HINT}</p>

              {!editingTemplateId && (
                <div
                  className="mb-4 rounded-lg border border-fuchsia-500/25 bg-fuchsia-500/10 p-3 space-y-2"
                  data-testid="email-template-ai-panel"
                >
                  <div className="flex items-center gap-2 text-xs font-medium text-fuchsia-100">
                    <Sparkles className="w-3.5 h-3.5" />
                    Generate with AI
                  </div>
                  <p className="text-[10px] text-fuchsia-100/75">
                    Describe your email — AI writes subject + HTML with merge tags. You can edit before saving.
                  </p>
                  <Textarea
                    value={aiPrompt}
                    onChange={(e) => setAiPrompt(e.target.value)}
                    rows={3}
                    className="text-xs bg-zinc-950 border-zinc-700 focus:border-fuchsia-500"
                    placeholder="e.g. Welcome email for new coffee shop subscribers with 10% off first order"
                    data-testid="email-template-ai-prompt"
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-[10px] text-zinc-400 mb-1">Tone</label>
                      <select
                        value={aiTone}
                        onChange={(e) => setAiTone(e.target.value)}
                        className="w-full h-8 rounded-md bg-zinc-950 border border-zinc-700 px-2 text-xs"
                      >
                        <option value="friendly">Friendly</option>
                        <option value="professional">Professional</option>
                        <option value="playful">Playful</option>
                        <option value="urgent">Urgent / sales</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-[10px] text-zinc-400 mb-1">Goal</label>
                      <select
                        value={aiGoal}
                        onChange={(e) => setAiGoal(e.target.value)}
                        className="w-full h-8 rounded-md bg-zinc-950 border border-zinc-700 px-2 text-xs"
                      >
                        <option value="promotional">Promotion / sale</option>
                        <option value="welcome">Welcome</option>
                        <option value="newsletter">Newsletter</option>
                        <option value="announcement">Announcement</option>
                        <option value="re-engagement">Win-back</option>
                      </select>
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <Button
                      type="button"
                      size="sm"
                      disabled={aiGenerating}
                      onClick={handleGenerateTemplateWithAi}
                      className="h-8 px-3 text-xs bg-fuchsia-600 hover:bg-fuchsia-500"
                      data-testid="email-template-ai-generate"
                    >
                      {aiGenerating ? (
                        <>
                          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                          Generating…
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-3 h-3 mr-1" />
                          Generate draft
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}

              <form onSubmit={handleCreateTemplate} className="space-y-2 text-xs">
                <div>
                  <label className="block text-[11px] text-zinc-400 mb-1">Name</label>
                  <Input
                    value={tplName}
                    onChange={(e) => setTplName(e.target.value)}
                    className="h-8 text-xs bg-zinc-900 border-zinc-700 focus:border-violet-500"
                    placeholder="Welcome Email"
                  />
                </div>
                <div>
                  <label className="block text-[11px] text-zinc-400 mb-1">Subject</label>
                  <Input
                    value={tplSubject}
                    onChange={(e) => setTplSubject(e.target.value)}
                    className="h-8 text-xs bg-zinc-900 border-zinc-700 focus:border-violet-500"
                    placeholder="Welcome to our newsletter!"
                  />
                </div>
                <div>
                  <label className="block text-[11px] text-zinc-400 mb-1">HTML Content</label>
                  <Textarea
                    value={tplHtml}
                    onChange={(e) => setTplHtml(e.target.value)}
                    rows={8}
                    className="text-xs bg-zinc-900 border-zinc-700 focus:border-violet-500 font-mono"
                    placeholder="<p>Hi {{first_name}}, welcome to {{brand_name}}...</p>"
                  />
                  {hasMergeTags(tplHtml) || hasMergeTags(tplSubject) ? (
                    <div className="mt-2 border border-zinc-700 rounded-lg overflow-hidden bg-white">
                      <div className="px-2 py-1 bg-zinc-100 text-[10px] text-zinc-600 border-b border-zinc-200">
                        Preview as {sampleMergeContext.first_name} · {sampleMergeContext.brand_name}
                      </div>
                      <iframe
                        title="Template preview"
                        srcDoc={renderMergeTemplate(tplHtml, sampleMergeContext)}
                        className="w-full h-48 border-0 bg-white"
                        sandbox=""
                      />
                    </div>
                  ) : null}
                </div>
                <div className="flex justify-end gap-2">
                  {editingTemplateId ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="h-8 px-3 text-xs"
                      onClick={() => {
                        setEditingTemplateId('');
                        setTplName('');
                        setTplSubject('');
                        setTplHtml('');
                      }}
                    >
                      Cancel
                    </Button>
                  ) : null}
                  <Button
                    type="submit"
                    size="sm"
                    className="h-8 px-3 text-xs bg-violet-600 hover:bg-violet-500"
                  >
                    {editingTemplateId ? 'Update Template' : 'Save Template'}
                  </Button>
                </div>
              </form>
            </section>

            <section className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
              <h2 className="text-sm font-semibold mb-2">Templates</h2>
              <div className="border border-zinc-800 rounded-lg overflow-hidden">
                <div className="px-3 py-2 bg-zinc-950/70 border-b border-zinc-800 text-[11px] text-zinc-400">
                  Saved Templates
                </div>
                <div className="max-h-80 overflow-y-auto">
                  {emailStandaloneTemplates.length === 0 ? (
                    <p className="text-[11px] text-zinc-500 p-3">Custom templates you save appear here. Starter funnel steps are listed above.</p>
                  ) : (
                    <Table className="w-full text-[11px]">
                      <TableHeader className="bg-zinc-900/70">
                        <TableRow>
                          <TableHead className="px-3 py-2 text-left">Name</TableHead>
                          <TableHead className="px-3 py-2 text-left">Subject</TableHead>
                          <TableHead className="px-3 py-2 text-right"> </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {emailStandaloneTemplates.map((t) => (
                          <TableRow key={t.id} className="border-t border-zinc-800">
                            <TableCell className="px-3 py-2 whitespace-nowrap">
                              {t.name}
                              {t.is_default_funnel && t.funnel_name && (
                                <span className="ml-1 text-[10px] text-violet-400">({t.funnel_name})</span>
                              )}
                            </TableCell>
                            <TableCell className="px-3 py-2 whitespace-nowrap text-zinc-500">
                              {t.subject || '-'}
                            </TableCell>
                            <TableCell className="px-3 py-2 text-right">
                              <button type="button" onClick={() => applyEmailTemplateToForm(t)} className="text-violet-300 hover:text-violet-100">Edit</button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </div>
              </div>
            </section>
          </div>
          </div>
        )}

        {activeTab === 'campaigns' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <section className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
              <h2 className="text-sm font-semibold mb-2">Create Campaign</h2>
              <form onSubmit={handleCreateCampaign} className="space-y-2 text-xs">
                <div>
                  <label className="block text-[11px] text-zinc-400 mb-1">Name</label>
                  <Input
                    value={campName}
                    onChange={(e) => setCampName(e.target.value)}
                    className="h-8 text-xs bg-zinc-900 border-zinc-700 focus:border-violet-500"
                    placeholder="Spring Promo"
                  />
                </div>
                <div>
                  <label className="block text-[11px] text-zinc-400 mb-1">Subject</label>
                  <Input
                    value={campSubject}
                    onChange={(e) => setCampSubject(e.target.value)}
                    className="h-8 text-xs bg-zinc-900 border-zinc-700 focus:border-violet-500"
                    placeholder="Save 20% this week"
                  />
                </div>
                <div className="flex items-center justify-between bg-zinc-950/60 border border-zinc-800 rounded-md px-2 py-1.5">
                  <label className="text-[11px] text-zinc-300 inline-flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={campAbTest}
                      onChange={(e) => setCampAbTest(e.target.checked)}
                      data-testid="email-camp-abtest"
                      className="accent-violet-500"
                    />
                    A/B test subject lines
                  </label>
                  <span className="text-[10px] text-zinc-500">50/50 split · winner by opens</span>
                </div>
                {campAbTest && (
                  <div data-testid="email-camp-subject-b">
                    <label className="block text-[11px] text-zinc-400 mb-1">Subject B (variant)</label>
                    <Input
                      value={campAbSubjectB}
                      onChange={(e) => setCampAbSubjectB(e.target.value)}
                      className="h-8 text-xs bg-zinc-900 border-zinc-700 focus:border-violet-500"
                      placeholder="20% off — last chance"
                    />
                  </div>
                )}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-[11px] text-zinc-400 mb-1">From Name</label>
                    <Input
                      value={campFromName}
                      onChange={(e) => setCampFromName(e.target.value)}
                      className="h-8 text-xs bg-zinc-900 border-zinc-700 focus:border-violet-500"
                      placeholder={fromNameDefault || currentWebsite?.name || 'Your Brand'}
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] text-zinc-400 mb-1">From Email</label>
                    <Input
                      type="email"
                      value={campFromEmail}
                      onChange={(e) => setCampFromEmail(e.target.value)}
                      className="h-8 text-xs bg-zinc-900 border-zinc-700 focus:border-violet-500"
                      placeholder={fromEmailDefault || 'no-reply@yourdomain.com'}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] text-zinc-400 mb-1">Send To List</label>
                  <select
                    value={campListId}
                    onChange={(e) => setCampListId(e.target.value)}
                    className="w-full h-8 rounded-md bg-zinc-900 border border-zinc-700 px-2 text-xs focus:outline-none focus:border-violet-500"
                  >
                    {lists.map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.name}
                      </option>
                    ))}
                  </select>
                  <p className="text-[11px] text-violet-200/90 mt-1.5 bg-violet-500/10 border border-violet-500/20 rounded px-2 py-1">
                    {campaignRecipientPreview()}
                  </p>
                </div>
                <div>
                  <label className="block text-[11px] text-zinc-400 mb-1">Send timing</label>
                  <div className="flex items-center gap-3 mb-2">
                    <label className="text-[11px] text-zinc-300 inline-flex items-center gap-1.5">
                      <input
                        type="radio"
                        name="campScheduleMode"
                        checked={campScheduleMode === 'now'}
                        onChange={() => setCampScheduleMode('now')}
                        className="accent-violet-500"
                      />
                      Save as draft (send manually)
                    </label>
                    <label className="text-[11px] text-zinc-300 inline-flex items-center gap-1.5">
                      <input
                        type="radio"
                        name="campScheduleMode"
                        checked={campScheduleMode === 'schedule'}
                        onChange={() => setCampScheduleMode('schedule')}
                        className="accent-violet-500"
                        data-testid="email-camp-schedule"
                      />
                      Schedule send
                    </label>
                  </div>
                  {campScheduleMode === 'schedule' && (
                    <Input
                      type="datetime-local"
                      value={campScheduledAt}
                      onChange={(e) => setCampScheduledAt(e.target.value)}
                      className="h-8 text-xs bg-zinc-900 border-zinc-700 focus:border-violet-500"
                      data-testid="email-camp-scheduled-at"
                    />
                  )}
                </div>
                <div>
                  <label className="block text-[11px] text-zinc-400 mb-1">HTML Content</label>
                  <p className="text-[10px] text-zinc-500 mb-1">{MERGE_TAG_HINT}</p>
                  <Textarea
                    value={campHtml}
                    onChange={(e) => setCampHtml(e.target.value)}
                    rows={8}
                    className="text-xs bg-zinc-900 border-zinc-700 focus:border-violet-500"
                    placeholder="<h1>Special Offer</h1><p>Hi {{first_name}}, details...</p>"
                  />
                  {hasMergeTags(campHtml) || hasMergeTags(campSubject) ? (
                    <div className="mt-2 border border-zinc-700 rounded-lg overflow-hidden bg-white">
                      <div className="px-2 py-1 bg-zinc-100 text-[10px] text-zinc-600 border-b border-zinc-200">
                        Live preview (sample: {sampleMergeContext.first_name} · {sampleMergeContext.brand_name})
                      </div>
                      <iframe
                        title="Campaign preview"
                        srcDoc={renderMergeTemplate(campHtml, sampleMergeContext)}
                        className="w-full h-64 border-0 bg-white"
                        sandbox=""
                      />
                    </div>
                  ) : null}
                </div>
                <div className="flex justify-end">
                  <Button
                    type="submit"
                    size="sm"
                    className="h-8 px-3 text-xs bg-violet-600 hover:bg-violet-500"
                  >
                    {campScheduleMode === 'schedule' ? 'Schedule Campaign' : 'Save Campaign'}
                  </Button>
                </div>
              </form>
            </section>

            <section className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
              <h2 className="text-sm font-semibold mb-2">Campaigns</h2>
              <div className="border border-zinc-800 rounded-lg overflow-hidden">
                <div className="px-3 py-2 bg-zinc-950/70 border-b border-zinc-800 text-[11px] text-zinc-400">
                  Campaigns
                </div>
                <div className="max-h-80 overflow-y-auto">
                  {campaigns.length === 0 ? (
                    <p className="text-[11px] text-zinc-500 p-3">No campaigns yet.</p>
                  ) : (
                    <Table className="w-full text-[11px]">
                      <TableHeader className="bg-zinc-900/70">
                        <TableRow>
                          <TableHead className="px-3 py-2 text-left">Name</TableHead>
                          <TableHead className="px-3 py-2 text-left">Subject</TableHead>
                          <TableHead className="px-3 py-2 text-left">Status</TableHead>
                          <TableHead className="px-3 py-2 text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {campaigns.map((c) => (
                          <TableRow
                            key={c.id}
                            data-campaign-id={c.id}
                            className="border-t border-zinc-800"
                          >
                            <TableCell className="px-3 py-2 whitespace-nowrap">{c.name}</TableCell>
                            <TableCell className="px-3 py-2 whitespace-nowrap text-zinc-500">
                              {c.subject || '-'}
                            </TableCell>
                            <TableCell className="px-3 py-2 whitespace-nowrap text-[10px] uppercase tracking-wide text-zinc-400">
                              <span
                                className={
                                  c.status === 'failed'
                                    ? 'text-red-400'
                                    : c.status === 'scheduled'
                                      ? 'text-amber-300'
                                      : c.status === 'sent'
                                        ? 'text-emerald-400'
                                        : ''
                                }
                              >
                                {c.status}
                              </span>
                              {c.status === 'scheduled' && c.scheduled_at ? (
                                <span className="block normal-case text-[9px] text-zinc-500 mt-0.5">
                                  {new Date(c.scheduled_at).toLocaleString()}
                                </span>
                              ) : null}
                              {c.status === 'failed' && (c.send_errors || []).length > 0 ? (
                                <span className="block normal-case text-[9px] text-red-300/90 mt-0.5 max-w-[180px] truncate" title={(c.send_errors || []).join('; ')}>
                                  {(c.send_errors || [])[0]}
                                </span>
                              ) : null}
                            </TableCell>
                            <TableCell className="px-3 py-2 whitespace-nowrap text-right">
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 px-3 text-[11px] border-violet-600 text-violet-400 hover:bg-violet-600/10"
                                disabled={sendingId === c.id || c.status === 'scheduled'}
                                onClick={() => handleSendCampaign(c.id)}
                              >
                                {sendingId === c.id ? (
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                ) : c.status === 'failed' ? (
                                  'Retry'
                                ) : (
                                  'Send Now'
                                )}
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </div>
              </div>
              <div className="mt-4 border border-zinc-800 rounded-lg overflow-hidden" data-testid="email-funnel-runs">
                <div className="px-3 py-2 bg-zinc-950/70 border-b border-zinc-800 text-[11px] text-zinc-400">
                  Automated follow-ups (3 emails per subscriber · stops on reply)
                </div>
                <div className="max-h-64 overflow-y-auto">
                  {funnelRuns.length === 0 ? (
                    <p className="text-[11px] text-zinc-500 p-3">
                      Nothing running yet. On Templates, click <strong>Start automated</strong> on a series to email your list over several days.
                    </p>
                  ) : (
                    <Table className="w-full text-[11px]">
                      <TableHeader className="bg-zinc-900/70">
                        <TableRow>
                          <TableHead className="px-3 py-2 text-left">Funnel</TableHead>
                          <TableHead className="px-3 py-2 text-left">Subscriber</TableHead>
                          <TableHead className="px-3 py-2 text-left">Step</TableHead>
                          <TableHead className="px-3 py-2 text-left">Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {funnelRuns.slice(0, 50).map((run) => (
                          <TableRow key={run.id} className="border-t border-zinc-800">
                            <TableCell className="px-3 py-2">{run.funnel_name || run.funnel_slug}</TableCell>
                            <TableCell className="px-3 py-2 text-zinc-500">{run.subscriber_email}</TableCell>
                            <TableCell className="px-3 py-2">
                              {run.current_step || 0}/{run.max_steps || 3}
                            </TableCell>
                            <TableCell className="px-3 py-2 text-[10px] uppercase tracking-wide">
                              <span
                                className={
                                  run.status === 'stopped_replied'
                                    ? 'text-amber-400'
                                    : run.status === 'completed'
                                      ? 'text-emerald-400'
                                      : run.status === 'failed'
                                        ? 'text-red-400'
                                        : 'text-violet-300'
                                }
                              >
                                {run.status === 'stopped_replied' ? 'stopped (replied)' : run.status}
                              </span>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </div>
              </div>
            </section>
          </div>
        )}

        {activeTab === 'analytics' && (
          <div className="space-y-4" data-testid="email-analytics-panel">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold inline-flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-violet-400" /> Email analytics (last 7 days)
              </h2>
              {analyticsLoading && <Loader2 className="w-3.5 h-3.5 animate-spin text-zinc-500" />}
            </div>
            {!analytics ? (
              <p className="text-xs text-zinc-500">No analytics yet. Send a campaign to populate this dashboard.</p>
            ) : (
              <>
                <p className="text-[10px] text-zinc-500 mb-3">
                  Delivered = successful sends. Clicks always count as opens. Pixel opens need images enabled in Gmail
                  and a public backend URL (<code className="text-zinc-400">PUBLIC_BACKEND_URL</code>) — localhost pixels
                  cannot load from Gmail&apos;s servers.
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { label: 'Sent', val: analytics.totals.sent, tone: 'violet' },
                    { label: 'Delivered', val: analytics.totals.delivered, tone: 'emerald' },
                    { label: 'Opens', val: analytics.totals.opens, tone: 'sky' },
                    { label: 'Clicks', val: analytics.totals.clicks, tone: 'rose' },
                  ].map((m) => (
                    <div key={m.label} className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-3" data-testid={`email-analytics-${m.label.toLowerCase()}`}>
                      <p className="text-2xl font-bold">{(m.val || 0).toLocaleString()}</p>
                      <p className="text-[10px] uppercase tracking-wider text-zinc-500 mt-1">{m.label}</p>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-3">
                    <p className="text-2xl font-bold text-emerald-300">{analytics.open_rate}%</p>
                    <p className="text-[10px] uppercase tracking-wider text-zinc-500 mt-1">Open rate</p>
                  </div>
                  <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-3">
                    <p className="text-2xl font-bold text-sky-300">{analytics.click_rate}%</p>
                    <p className="text-[10px] uppercase tracking-wider text-zinc-500 mt-1">Click rate</p>
                  </div>
                  <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-3">
                    <p className="text-2xl font-bold text-rose-300">{analytics.bounce_rate}%</p>
                    <p className="text-[10px] uppercase tracking-wider text-zinc-500 mt-1">Bounce rate</p>
                  </div>
                </div>
                <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-3">
                  <h3 className="text-xs font-semibold mb-2">Top campaigns by opens</h3>
                  {analytics.top_campaigns?.length ? (
                    <table className="w-full text-[11px]">
                      <thead className="text-zinc-500">
                        <tr><th className="text-left py-1">Name</th><th className="text-left py-1">Sent</th><th className="text-left py-1">Opens</th><th className="text-left py-1">Clicks</th></tr>
                      </thead>
                      <tbody>
                        {analytics.top_campaigns.map((c) => (
                          <tr key={c.id} className="border-t border-zinc-800">
                            <td className="py-1">{c.name}</td>
                            <td className="py-1">{c.sent_count}</td>
                            <td className="py-1 text-emerald-300">{c.opens_count}</td>
                            <td className="py-1 text-sky-300">{c.clicks_count}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className="text-[11px] text-zinc-500 italic">No campaigns sent yet.</p>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {activeTab === 'blacklist' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="email-blacklist-panel">
            <section className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 text-xs space-y-2">
              <h2 className="text-sm font-semibold inline-flex items-center gap-2 mb-1">
                <Ban className="w-4 h-4 text-rose-400" /> Add email to suppression list
              </h2>
              <p className="text-[11px] text-zinc-500 mb-2">
                Suppressed emails are excluded from every future campaign send. Common reasons: hard bounce, complaint, unsubscribe.
              </p>
              <Input
                value={blacklistEmail}
                onChange={(e) => setBlacklistEmail(e.target.value)}
                placeholder="bouncing@example.com"
                className="h-8 text-xs bg-zinc-900 border-zinc-700 focus:border-violet-500"
                data-testid="email-blacklist-email"
              />
              <select
                value={blacklistReason}
                onChange={(e) => setBlacklistReason(e.target.value)}
                className="w-full h-8 rounded-md bg-zinc-900 border border-zinc-700 px-2 text-xs focus:outline-none focus:border-violet-500"
                data-testid="email-blacklist-reason"
              >
                <option value="manual">Manual block</option>
                <option value="hard_bounce">Hard bounce</option>
                <option value="complaint">Spam complaint</option>
                <option value="unsubscribe">Unsubscribed</option>
              </select>
              <Button
                size="sm"
                onClick={addBlacklist}
                disabled={blacklistSaving || !blacklistEmail.trim()}
                data-testid="email-blacklist-submit"
                className="h-8 px-3 text-xs bg-rose-600 hover:bg-rose-500 disabled:opacity-50"
              >
                {blacklistSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3 mr-1" />}
                Add to suppression list
              </Button>
            </section>
            <section className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 text-xs">
              <h2 className="text-sm font-semibold mb-2">Suppressed emails ({blacklist.length})</h2>
              {blacklist.length === 0 ? (
                <p className="text-[11px] text-zinc-500">No suppressed emails yet.</p>
              ) : (
                <div className="max-h-80 overflow-auto pr-1 space-y-1">
                  {blacklist.map((b) => (
                    <div key={b.id} className="flex items-center justify-between border border-zinc-800 rounded px-2 py-1.5" data-testid={`email-blacklist-item-${b.id.slice(0, 8)}`}>
                      <div className="min-w-0">
                        <p className="text-[11px] truncate text-white">{b.email}</p>
                        <p className="text-[10px] text-zinc-500 uppercase tracking-wider">{b.reason}</p>
                      </div>
                      <button
                        onClick={() => removeBlacklist(b.id)}
                        className="text-[10px] text-zinc-500 hover:text-rose-400 inline-flex items-center gap-1"
                      >
                        <Trash2 className="w-3 h-3" /> Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>
        )}
      </main>
    </div>
  );
};

export default EmailMarketingDashboard;
